<template>
  <div class="page-wrap updateVirtualMembershipCard">
    <v-form ref="form" v-model="valid" @submit.prevent="checkForm">
      <v-card class="tool-card" elevation="2">
        <div class="tool-card__accent"></div>

        <v-card-item class="pt-5">
          <template v-slot:prepend>
            <v-avatar color="secondary" rounded="lg" size="44">
              <v-icon icon="mdi-wallet-membership" color="#231F20" size="24"></v-icon>
            </v-avatar>
          </template>
          <v-card-title class="text-h6">Update Virtual Membership Card</v-card-title>
          <v-card-subtitle class="tool-card__subtitle">
            Correct inaccuracies in Virtual Membership Card values so they reflect the accurate information.
          </v-card-subtitle>
        </v-card-item>

        <v-divider class="mt-4"></v-divider>

        <v-card-text class="pt-6 pb-2">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field v-model="membershipNumber" label="Membership Number" :rules="membershipNumberRules"
                @input="clearState()" prepend-inner-icon="mdi-pound" inputmode="numeric"></v-text-field>
            </v-col>
          </v-row>
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer></v-spacer>
          <v-btn type="submit" :loading="verifying" :disabled="verifying" color="primary" variant="flat" size="large">
            Verify Member's Account
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Dialog to confirm correct account and continue processing the VCard membership -->
      <v-dialog v-model="dialog" max-width="480" persistent>
        <v-card class="tool-card">
          <div class="tool-card__accent"></div>
          <v-card-item class="pt-5">
            <template v-slot:prepend>
              <v-avatar color="secondary" rounded="lg" size="40">
                <v-icon icon="mdi-alert-box-outline" color="#231F20"></v-icon>
              </v-avatar>
            </template>
            <v-card-title class="text-h6">Process Virtual Card Update?</v-card-title>
          </v-card-item>

          <v-card-text class="pt-2">
            <div class="confirm-row">
              <span class="confirm-label">Membership Number</span>
              <span class="confirm-value">{{ accountResp && accountResp.membershipNumber }}</span>
            </div>
            <v-divider class="my-3"></v-divider>
            <div class="confirm-row">
              <span class="confirm-label">Account Name</span>
              <span class="confirm-value">{{ accountResp && accountResp.accountName }}</span>
            </div>
          </v-card-text>

          <v-card-actions class="px-4 pb-4">
            <v-btn variant="text" @click="dialog = false">Cancel</v-btn>
            <v-spacer></v-spacer>
            <v-btn color="primary" variant="flat" size="large" @click="confirmUpdate">
              Update Card
              <v-icon icon="mdi-arrow-right" end></v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-alert v-if="accountError" :text="accountError" type="error" variant="tonal"
        icon="mdi-alert-circle-outline" class="text-start mt-6"></v-alert>

      <!-- Loader Dialog -->
      <v-dialog v-model="loaderDialog" max-width="320" persistent>
        <v-list class="py-2" elevation="12" rounded="lg">
          <v-list-item prepend-icon="mdi-ferry" title="Updating Membership...">
            <template v-slot:append>
              <v-progress-circular color="primary" indeterminate size="16" width="2"></v-progress-circular>
            </template>
          </v-list-item>
        </v-list>
      </v-dialog>
      <!-- end loader dialog -->
    </v-form>

    <div v-if="alertMessage" class="mt-6">
      <v-alert :text="alertMessage" :type="type" variant="tonal"
        :icon="type === 'success' ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline'"
        class="text-start"></v-alert>
      <v-alert v-if="type === 'success'" title="Take Action"
        text="Send the Customer A Digital Membership Card Email in Salesforce" type="info" variant="tonal"
        icon="mdi-email-outline" class="text-start mt-4"></v-alert>
    </div>
  </div>
</template>


<script>
import api, { responseErrorMessage } from "../services/api";
import { membershipNumberRules } from "../utils/validators";

export default {
  name: "UpdateVirtualMembershipCard",
  data() {
    return {
      membershipNumberRules,
      loaderDialog: false,
      dialog: false,
      verifying: false,
      membershipNumber: null,
      accountResp: null,
      accountError: null,
      valid: null,
      type: null,
      alertMessage: null,
      virtualCardEndpoint: 'updateVirtualCard',
      accountNameEndpoint: 'accountNameFromMembership'
    }
  },
  methods: {
    // validate via the form's shared rules before hitting the API
    async checkForm() {
      const { valid } = await this.$refs.form.validate();
      if (valid) await this.getAccountName();
    },
    confirmUpdate() {
      this.dialog = false;
      this.loaderDialog = true;
      this.updateVirtualCard();
    },
    async updateVirtualCard() {
      this.alertMessage = null;
      this.type = null;

      try {
        // validateStatus: keep 4xx out of catch so specific alerts can render
        const resp = await api.post(
          `/${this.virtualCardEndpoint}`,
          { membershipNumber: this.membershipNumber },
          { validateStatus: () => true }
        );
        if (resp.status === 200) {
          this.buildCCResponse(resp.data);
          this.setType(resp.data);
        } else {
          this.type = "error";
          this.alertMessage = responseErrorMessage(
            resp,
            "The card update could not be completed. Try again."
          );
        }
      } catch (e) {
        // network error
        console.error("Error when updating Cards", e)
        this.type = "error";
        this.alertMessage =
          "Could not reach the server. Check connection/VPN and try again.";
      } finally {
        // reset() clears the field *and* its validation state — plain
        // `membershipNumber = null` left the required-field rule showing a red
        // error immediately after a successful update.
        this.$refs.form?.reset();
        this.accountResp = null;
        this.accountError = null;
        this.loaderDialog = false;
      }
    },
    async getAccountName() {
      this.accountResp = null;
      this.accountError = null;
      this.verifying = true;

      try {
        const resp = await api.get(
          `/${this.accountNameEndpoint}/${encodeURIComponent(this.membershipNumber)}`,
          { validateStatus: () => true }
        );

        if (resp.status === 200) {
          this.accountResp = resp.data;
          this.dialog = true;
        } else {
          this.accountError = responseErrorMessage(
            resp,
            "Invalid Membership Number, try to re-enter the Membership Number"
          );
        }
      } catch (e) {
        // network error
        console.error("Error fetching account name", e)
        this.accountError =
          "Could not reach the server. Check connection/VPN and try again.";
      } finally {
        this.verifying = false;
      }
    },
    // Any edit to the membership number invalidates whatever is on screen.
    clearState() {
      this.alertMessage = null;
      this.type = null;
      this.accountResp = null;
      this.accountError = null;
    },
    buildCCResponse(virtualCardResp) {
      switch (virtualCardResp['status_code']) {
        case 201:
        case 400:
        case 404:
          this.alertMessage = `${virtualCardResp['data']}`;
          break;
        case 500:
          this.alertMessage = "Something Went Wrong Try Again"
          break;
        default:
          this.alertMessage = "Something Went Wrong with the request"
          break;
      }
    },
    setType(virtualCardResp) {
      switch (virtualCardResp['status_code']) {
        case 201:
          this.type = "success"
          break;
        default:
          this.type = "error";
          break;
      }
    }
  }
};
</script>

<style scoped>
.confirm-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
}

.confirm-label {
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.7;
}

.confirm-value {
  font-size: 1.0625rem;
  font-weight: 600;
  text-align: right;
}
</style>
